import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Icon,
  Button,
  useToast,
  Flex,
  Circle,
  IconButton
} from '@chakra-ui/react';
import { FiArrowLeft, FiArrowRight, FiCheckCircle, FiExternalLink, FiFileText, FiInfo, FiVideo } from 'react-icons/fi';
import { IoWarningOutline } from "react-icons/io5";
import { ContentRequirement, Campaign, Collab } from '@foodfluence/shared';
import { useLocation, useNavigate } from 'react-router-dom';
import { getCollabContentRequirements, getContentTypeDisplayName, getContentDurationText, getContentTypeCount, getContentPreset, getContentPresetDisplayName, isVideoRequirement } from '../../../utils/contentUtils';
import { getChannelDisplayName, getChannelIcon } from '../../../utils/channelUtils';
import { getContentBriefFileUrl } from '../../../services/firebaseService';
import { ff } from '../../../theme';

const cardProps = { bg: 'white', borderRadius: '20px', borderWidth: '1px', borderColor: ff.line, p: 5 } as const;

const SectionTitle: React.FC<{ icon: React.ElementType; color?: string; children: React.ReactNode }> = ({ icon, color = ff.ink, children }) => (
  <HStack spacing={2.5} mb={4}>
    <Icon as={icon} color={color} boxSize={5} />
    <Text fontSize="16px" fontWeight="600" color={ff.ink}>
      {children}
    </Text>
  </HStack>
);

const Bullet: React.FC<{ color?: string; children: React.ReactNode; preWrap?: boolean }> = ({ color = ff.ink, children, preWrap }) => (
  <HStack spacing={3} align="start">
    <Circle size="6px" bg={color} mt="9px" flexShrink={0} />
    <Text fontSize="15px" color={ff.body} lineHeight="1.5" whiteSpace={preWrap ? 'pre-wrap' : undefined}>{children}</Text>
  </HStack>
);

const ContentGuidelinesPage: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  // Get data from navigation state
  const { requirement, campaign, collab, clientName, returnPath, returnState, initialRequirementIndex } = location.state as {
    requirement: ContentRequirement,
    campaign: Campaign,
    collab?: Collab,
    clientName: string,
    returnPath: string,
    returnState?: any,
    initialRequirementIndex?: number
  };

  const resolvedContentRequirements = getCollabContentRequirements(collab, campaign);
  const allRequirements = resolvedContentRequirements
    ? Object.values(resolvedContentRequirements)
    : [requirement];
  const totalRequirements = allRequirements.length;
  
  // Initialize currentIndex with the clicked requirement index, or 0 if not specified
  const [currentIndex, setCurrentIndex] = useState(initialRequirementIndex || 0);
  const currentRequirement = allRequirements[currentIndex];



  const handleFileOpen = async (fileName: string) => {
    if (!fileName) {
      toast({
        title: "No file available",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      // Extract the actual filename from the path if it's a full path
      const actualFileName = fileName.split('/').pop() || fileName;
      
      // Get the requirement ID from the current requirement
      const requirementId = Object.entries(resolvedContentRequirements || {})
        .find(([_, req]) => req === currentRequirement)?.[0];

      if (!requirementId) {
        console.error('Could not find requirement ID', {
          resolvedContentRequirements,
          currentRequirement
        });
        throw new Error('Could not find requirement ID');
      }

      console.log('Fetching content brief with params:', {
        clientId: campaign.clientID,
        requirementId,
        fileName: actualFileName
      });

      const url = await getContentBriefFileUrl(
        campaign.clientID,
        requirementId,
        actualFileName
      );
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error opening content brief:', error);
      toast({
        title: "Error opening file",
        description: "Could not open the content brief file. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentIndex < totalRequirements - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handleDotClick = (index: number) => {
    setCurrentIndex(index);
  };

  const handleBack = () => {
    console.log('Navigating to returnPath:', returnPath);
    
    // If we have a returnPath, navigate back to it
    if (returnPath) {
      navigate(returnPath, { 
        replace: true,
        state: returnState
      });
    } else {
      // Fallback to request collab page if no returnPath is provided
      navigate('/explore/request-collab', { 
        replace: true,
        state: {
          campaign,
          client: location.state?.client,
          requestedCollab: location.state?.requestedCollab
        }
      });
    }
  };

  // Update current requirement when index changes
  useEffect(() => {
    // This will automatically update when currentIndex changes
  }, [currentIndex]);

  // If no data is available, show error or redirect
  if (!requirement || !campaign) {
    return (
      <Box minH="100vh" bg={ff.grey} p={4}>
        <VStack spacing={4} align="center" justify="center" minH="100vh">
          <Text fontSize="16px" color={ff.text2}>No content requirements found.</Text>
          <Button variant="ink" px={8} onClick={handleBack}>Go back</Button>
        </VStack>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg={ff.grey}>
      {/* Header */}
      <Box bg="white" px={2} py={2} borderBottomWidth="1px" borderColor={ff.line}>
        <HStack justify="space-between" align="center" pr={2}>
          <HStack spacing={1}>
            <IconButton
              icon={<Icon as={FiArrowLeft} boxSize={5} />}
              variant="ghost"
              onClick={handleBack}
              w="44px"
              h="44px"
              borderRadius="full"
              aria-label="Back"
              color={ff.ink}
            />
            <Text textStyle="display" fontSize="20px" color={ff.ink}>
              Review requirements
            </Text>
          </HStack>
          <Box bg={ff.grey} px={3} py={1} borderRadius="full">
            <Text fontSize="13px" color={ff.ink} fontWeight="600">
              {currentIndex + 1} of {totalRequirements}
            </Text>
          </Box>
        </HStack>
      </Box>

      <Box p={4} pb={totalRequirements > 1 ? "96px" : "16px"}>
        <VStack spacing={3} align="stretch">
          {/* Content Type Card */}
          <Box {...cardProps}>
            <VStack align="stretch" spacing={4}>
              <HStack spacing={3} align="center">
                <Flex
                  bg={ff.ink}
                  borderRadius="14px"
                  boxSize="48px"
                  align="center"
                  justify="center"
                  flexShrink={0}
                >
                  <Icon as={FiVideo} boxSize={5} color={ff.lime} />
                </Flex>
                <VStack align="start" spacing={0.5}>
                  <Text fontSize="17px" fontWeight="600" color={ff.ink}>
                    {getContentTypeCount(currentRequirement)}x {getContentPresetDisplayName(getContentPreset(currentRequirement.duration, currentRequirement.type))}
                  </Text>
                  {currentRequirement.duration && isVideoRequirement(currentRequirement) && (
                    <Text fontSize="14px" color={ff.text2}>
                      {getContentDurationText(currentRequirement.duration)}
                    </Text>
                  )}
                </VStack>
              </HStack>

              {currentRequirement.channels && currentRequirement.channels.length > 0 && (
                <Flex gap={2} flexWrap="wrap">
                  {currentRequirement.channels.map((channel) => {
                    const ChannelIcon = getChannelIcon(channel);
                    return (
                      <HStack
                        key={channel}
                        spacing={2}
                        bg={ff.grey}
                        px={3}
                        py={1.5}
                        borderRadius="full"
                      >
                        <Icon as={ChannelIcon} boxSize={4} color={ff.ink} />
                        <Text fontSize="13px" color={ff.ink} fontWeight="500">
                          {getChannelDisplayName(channel)}
                        </Text>
                      </HStack>
                    );
                  })}
                </Flex>
              )}
            </VStack>
          </Box>

          {/* Inspiration links + content brief file as one divided card */}
          {((currentRequirement.inspirationLinks?.length ?? 0) > 0 || currentRequirement.contentBriefFile) && (
            <Box bg="white" borderRadius="20px" borderWidth="1px" borderColor={ff.line} overflow="hidden">
              {currentRequirement.inspirationLinks?.map((url, idx) => (
                <HStack
                  key={idx}
                  as="button"
                  type="button"
                  w="100%"
                  textAlign="left"
                  spacing={3}
                  px={5}
                  minH="56px"
                  borderTopWidth={idx ? '1px' : 0}
                  borderColor={ff.line}
                  _hover={{ bg: ff.grey }}
                  transition="all 0.2s"
                  onClick={() => window.open(url, '_blank')}
                >
                  <Icon as={FiExternalLink} color={ff.ink} boxSize={4} />
                  <Text flex={1} fontSize="15px" color={ff.ink} fontWeight="600">
                    Inspiration example
                  </Text>
                </HStack>
              ))}
              {currentRequirement.contentBriefFile && (
                <HStack
                  as="button"
                  type="button"
                  w="100%"
                  textAlign="left"
                  spacing={3}
                  px={5}
                  minH="56px"
                  borderTopWidth={(currentRequirement.inspirationLinks?.length ?? 0) > 0 ? '1px' : 0}
                  borderColor={ff.line}
                  _hover={{ bg: ff.grey }}
                  transition="all 0.2s"
                  onClick={() => handleFileOpen(currentRequirement.contentBriefFile!)}
                >
                  <Icon as={FiFileText} color={ff.ink} boxSize={4} />
                  <Text flex={1} fontSize="15px" color={ff.ink} fontWeight="600">
                    View content brief
                  </Text>
                </HStack>
              )}
            </Box>
          )}

          {/* Must Include Section - Only show if there are actual must-include items */}
          {currentRequirement.guidelines?.mustInclude && currentRequirement.guidelines.mustInclude.length > 0 && (
            <Box {...cardProps}>
              <SectionTitle icon={FiCheckCircle}>
                Must include ({currentRequirement.guidelines.mustInclude.length} items)
              </SectionTitle>
              <VStack align="start" spacing={3}>
                {currentRequirement.guidelines.mustInclude.map((item, idx) => (
                  <Bullet key={idx}>{item}</Bullet>
                ))}
              </VStack>
            </Box>
          )}

          {/* Critical Reminders Section - Only show if there are actual don'ts */}
          {currentRequirement.guidelines?.donts && currentRequirement.guidelines.donts.length > 0 && (
            <Box {...cardProps}>
              <SectionTitle icon={IoWarningOutline} color={ff.danger}>
                Critical reminders ({currentRequirement.guidelines.donts.length} items)
              </SectionTitle>
              <VStack align="start" spacing={3}>
                {/* Don'ts */}
                {currentRequirement.guidelines.donts.map((item, idx) => (
                  <Bullet key={idx} color={ff.danger}>{item}</Bullet>
                ))}
              </VStack>
            </Box>
          )}

          {/* Additional Info Section - Only show if there's actual additional info */}
          {currentRequirement.guidelines?.additionalInfo && (
            <Box {...cardProps}>
              <SectionTitle icon={FiInfo}>
                Important notes
              </SectionTitle>
              <Bullet preWrap>{currentRequirement.guidelines.additionalInfo}</Bullet>
            </Box>
          )}

          {/* Do's Section - Only show if there are actual do's */}
          {currentRequirement.guidelines?.dos && currentRequirement.guidelines.dos.length > 0 && (
            <Box {...cardProps}>
              <SectionTitle icon={FiCheckCircle}>
                Do's ({currentRequirement.guidelines.dos.length} items)
              </SectionTitle>
              <VStack align="start" spacing={3}>
                {currentRequirement.guidelines.dos.map((item, idx) => (
                  <Bullet key={idx}>{item}</Bullet>
                ))}
              </VStack>
            </Box>
          )}

          {/* Additional Information - Only show if there's actual additional info */}
          {currentRequirement.additionalInformation && (
            <Box {...cardProps}>
              <Text fontSize="16px" fontWeight="600" color={ff.ink} mb={3}>
                Additional information
              </Text>
              <Text fontSize="15px" color={ff.body} lineHeight="1.5" whiteSpace="pre-wrap">
                {currentRequirement.additionalInformation}
              </Text>
            </Box>
          )}
        </VStack>
      </Box>

      {/* Pagination Controls - Fixed at bottom of page */}
      {totalRequirements > 1 && (
        <Box
          position="fixed"
          bottom={0}
          left={0}
          right={0}
          bg="white"
          borderTopWidth="1px"
          borderColor={ff.line}
          px={4}
          pt={2}
          pb="calc(8px + env(safe-area-inset-bottom))"
          zIndex={10}
        >
          <HStack spacing={2} justify="center">
            <IconButton
              icon={<Icon as={FiArrowLeft} boxSize={5} />}
              variant="ghost"
              w="44px"
              h="44px"
              borderRadius="full"
              aria-label="Previous"
              color={ff.ink}
              onClick={handlePrevious}
              isDisabled={currentIndex === 0}
            />
            <HStack spacing={0}>
              {Array.from({ length: totalRequirements }, (_, idx) => (
                <Flex
                  key={idx}
                  w="24px"
                  h="44px"
                  align="center"
                  justify="center"
                  cursor="pointer"
                  onClick={() => handleDotClick(idx)}
                >
                  <Box
                    w={idx === currentIndex ? '18px' : '8px'}
                    h="8px"
                    borderRadius="full"
                    bg={idx === currentIndex ? ff.ink : ff.border}
                    transition="all 0.2s"
                  />
                </Flex>
              ))}
            </HStack>
            <IconButton
              icon={<Icon as={FiArrowRight} boxSize={5} />}
              variant="ghost"
              w="44px"
              h="44px"
              borderRadius="full"
              aria-label="Next"
              color={ff.ink}
              onClick={handleNext}
              isDisabled={currentIndex === totalRequirements - 1}
            />
          </HStack>
        </Box>
      )}
    </Box>
  );
};

export default ContentGuidelinesPage; 